import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectOneSetting, selectOverview } from "../../../util/store/selectors";
import { ILevelByVessel, IOperations, IVessel } from "../../../util/varibles/interface";
import { OverviewSetting } from "../../../util/varibles/userSetting";
import AvailableTimes from "../../PlanOperationPage/Plan/Calendar/AvailableTimes";
import { calculateWidth } from "../../PlanOperationPage/Plan/Calendar/constants";
import Contracts from "../../PlanOperationPage/Plan/Calendar/Contracts";
import Layout from "../../PlanOperationPage/Plan/Calendar/Vessels/Layout";
import Vessel from "../../PlanOperationPage/Plan/Calendar/Vessels/Vessel";
import VesselName from "../../PlanOperationPage/Plan/Calendar/Vessels/VesselName";
import { upLevel } from "../../PlanOperationPage/util";
import { CALENDAR_ID } from "../constants";
import LazyLoading from './LazyLoading';
import Operations from "./Operations";

const Vessels: React.FC<{ vessel: IVessel }> = React.memo(({ vessel }) => {
    const level = useSelector(selectOverview.level);
    const wellboats = useSelector(selectOverview.wellboats);
    const showAllPlans = useSelector(selectOverview.showAllPlans);
    const crewVesselFilter = useSelector(selectOneSetting(OverviewSetting.CREW_VESSEL_FILTER)) as string[];

    const { vessels, operations, wellboatLevel, width, calendarWidth } = useMemo(() => {
        const normalizedWellboats = wellboats.vessels;
        const selectedIds = new Set(crewVesselFilter.filter(id => id !== vessel.id));
        const extraVessels = showAllPlans
            ? normalizedWellboats.filter(item => item.id !== vessel.id
                && (selectedIds.size === 0 || selectedIds.has(item.id)))
            : [];
        const vessels = [vessel, ...extraVessels];
        const width = calculateWidth(vessels.length);
        const operations: IOperations = Object.values(wellboats.operations).reduce((items, item) => {
            const operationVessel = normalizedWellboats.find(sub => sub.id === item.operation.vessel_id);
            if (operationVessel)
                items[item.operation.id] = { ...item, vessel: operationVessel };
            return items;
        }, {} as IOperations);
        const wellboatLevel = Object.values(operations).reduce(
            (items, item) => upLevel(items, item, operations),
            {} as ILevelByVessel,
        );

        return {
            vessels,
            operations,
            wellboatLevel,
            width,
            calendarWidth: width * vessels.length,
        };
    }, [showAllPlans, vessel, crewVesselFilter, wellboats]);

    return <>
        {vessels.map((item, index) => {
            const isWellboat = item.id !== vessel.id;
            return <Vessel
                key={item.id}
                width={width}
                vessel={item}
                visible
                index={index}
                calendarId={CALENDAR_ID}
                isAllowDrag={showAllPlans}
                background={isWellboat ? 'var(--bgWellboatPlan)' : undefined}
                borderLeft={isWellboat ? '1px solid var(--borderWellboatPlan)' : undefined}
                allowAction={!isWellboat}
            >
                <VesselName vessel={item} showAccessStatus={!isWellboat} />
                <Operations
                    width={width}
                    vesselIndex={index}
                    vessel={item}
                    level={isWellboat ? wellboatLevel[item.id] : level[item.id]}
                    operations={isWellboat ? operations : undefined}
                    allowEdit={!isWellboat}
                    ignoreUnavailable={isWellboat}
                />
                {!isWellboat && <AvailableTimes
                    width={width}
                    available_times={item.available_times || []}
                    vesselIndex={index}
                    vessel={item}
                />}
                {!isWellboat && <Contracts
                    vessel={item}
                    width={width}
                    contracts={item.contracts || []}
                    vesselIndex={index}
                />}
            </Vessel>;
        })}
        <LazyLoading calendarWidth={calendarWidth} />
        <Layout calendarWidth={calendarWidth} />
    </>
});

export default Vessels;
